import { AttachFile, Cancel, CheckCircle, CheckCircleOutline, Close } from '@mui/icons-material';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { isFirefox } from 'react-device-detect';
import { useDropzone } from 'react-dropzone';
import { IoMdDocument } from 'react-icons/io';
import Popup from 'reactjs-popup';
import { SelectedFile } from '../../interfaces/interfaces';
import { completeUploadUrl, fileUpload, generateUploadUrl, signedUpload, signupFileUpload } from '../../services/services';
import { ProgressBar } from './ProgressBar';
import VyButton from './VyButton';
import { VyCropper } from './VyCropper';
import VyModal from './VyModal';
import VyTextField from './VyTextField';
import Image from 'next/image';

const baseStyle = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  padding: '60px 120px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out',
};

interface VyUploadModalProps {
  onUpload?: (files: File[], url?: string) => void;
  onSingleUpload?: (file: SelectedFile) => Promise<void>;
  onMultiFilesUpload?: (files: SelectedFile[]) => Promise<void> | void;
  onClose?: () => void;
  open?: boolean;
  uploadMultiple?: boolean;
  showUploadProgress?: boolean;
  acceptedTypes?: string;
  withUrl?: boolean;
  isSignupUpload?: boolean;
  eventId?: string;
  withCrop?: boolean;
  cropAspectRatio?: number;
  useSignedUpload?: boolean;
}

const StatusIcon = (status?: string) => {
  let statusIcon;
  switch (status) {
    case 'SUCCESS':
      statusIcon = <CheckCircle htmlColor="green" />;
      break;
    case 'FAILURE':
      statusIcon = <Cancel color="error" />;
      break;
    default:
      statusIcon = <CheckCircleOutline color="primary" />;
      break;
  }
  return statusIcon;
};
const VyUploadModal = ({
  onUpload,
  onSingleUpload,
  onMultiFilesUpload,
  open = false,
  onClose,
  uploadMultiple = false,
  withUrl = true,
  showUploadProgress = false,
  acceptedTypes = 'image/*',
  isSignupUpload = false,
  eventId,
  withCrop = false,
  cropAspectRatio = 1,
  useSignedUpload = false,
}: VyUploadModalProps) => {
  const { t } = useTranslation(['booth']);
  const [previews, setPreviews] = useState<string[]>([]);
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const [currentCropFileIndex, setCurrentCropFileIndex] = useState<number>(-1);
  const [error, setError] = useState<string>('');
  const [urlValid, setUrlValid] = useState<boolean>(false);

  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setSelectedFiles([]);
  }, [open]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!(acceptedFiles.length > 0)) {
        console.log('no accepted file');
        return;
      }
      if (showUploadProgress) {
        let formattedFiles = [...acceptedFiles] as SelectedFile[];
        // if you directy use URL.createObjectURL in img or video source, it will flicker on every component rerender
        // setting it at the beggining of file picking will prevent that
        formattedFiles = formattedFiles.map((file) => {
          file.src = URL.createObjectURL(file);
          return file;
        });
        setSelectedFiles([...selectedFiles, ...formattedFiles]);
      } else setNewPreviews(acceptedFiles);
      setCurrentUrl('');
    },
    [selectedFiles]
  );

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, acceptedFiles, inputRef } = useDropzone({
    onDrop,
    multiple: uploadMultiple,
    accept: acceptedTypes,
  });

  function setNewPreviews(files: File[]) {
    if (previews.length > 0) previews.map((preview) => URL.revokeObjectURL(preview));
    setPreviews(files.map((file) => URL.createObjectURL(file)));
  }

  function dropStyle() {
    const dragMapper = {
      '#2196f3': isDragActive,
      '#00e676': isDragAccept,
      '#ff1744': isDragReject,
    };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const dragMapperKeyValue = Object.entries(dragMapper).find(([_, v]) => v);
    const borderColor = dragMapperKeyValue?.[0] || 'inherit';
    return { ...baseStyle, borderColor };
  }

  function clearSelection() {
    acceptedFiles.length = 0;
    acceptedFiles.splice(0, acceptedFiles.length);
    if (inputRef.current) inputRef.current.value = '';
    setPreviews([]);
  }

  function handleUpload() {
    if (!currentUrl && !(acceptedFiles.length > 0)) {
      setError(t('booth:provide-image-error'));
      return;
    }
    if (currentUrl && !urlValid) {
      setError(t('booth:url-invalid'));
    } else {
      setError('');
      onUpload?.(acceptedFiles, currentUrl);
      onClose?.();
    }
  }

  async function onCropImage(image: Blob) {
    //@ts-ignore
    await singleFileUpload(image, currentCropFileIndex);
    const nextIndex = selectedFiles.findIndex((file) => !file.progress || file.progress === 0);
    setCurrentCropFileIndex(nextIndex);
    if (nextIndex < 0) await onMultiFilesUpload?.(selectedFiles);
  }

  async function handleProgressiveUpload() {
    if (!selectedFiles || selectedFiles.length < 1) return;

    if (withCrop) {
      setCurrentCropFileIndex(selectedFiles.findIndex((file) => !file.progress || file.progress === 0));
      return;
    }

    setIsUploading(true);

    await Promise.all(
      selectedFiles.map(async (file, index, allFiles) => {
        if (selectedFiles[index].progress === 100) return;
        return singleFileUpload(allFiles[index], index);
      })
    );
    setIsUploading(false);
    await onMultiFilesUpload?.(selectedFiles);
  }

  async function singleFileUpload(file: File, fileIndex: number) {
    const files = [...selectedFiles];
    let fileId;

    const onProgressCallback = (progress: number) => {
      files[fileIndex].progress = progress;
      setSelectedFiles([...files]);
    };

    let uploadedFileData: any;
    if (useSignedUpload) {
      const { urls, tempFileName } = await generateUploadUrl(file.name ?? new Date().getTime().toString(), file.type);

      if (urls.length === 1) {
        try {
          await signedUpload(urls[0], file, onProgressCallback);
          uploadedFileData = await completeUploadUrl(tempFileName);
          fileId = uploadedFileData.fileId;
        } catch (err: any) {
          console.error(err);
          setIsUploading(false);
        }
      }
    } else {
      const uploadFunction = isSignupUpload && eventId ? await signupFileUpload(file, eventId, onProgressCallback) : await fileUpload(file, onProgressCallback);

      try {
        const { data: uploadedFile } = await uploadFunction;
        uploadedFileData = uploadedFile;
      } catch (err) {
        console.error(err);
        setIsUploading(false);
      }
    }

    files[fileIndex].fileId = uploadedFileData.fileId;
    fileId = uploadedFileData.fileId;
    if (!onSingleUpload && fileId) files[fileIndex].uploadState = 'SUCCESS';
    setSelectedFiles([...files]);

    try {
      if (fileId) {
        await onSingleUpload?.(file);
        files[fileIndex].uploadState = 'SUCCESS';
        setSelectedFiles([...files]);
      } else {
        files[fileIndex].uploadState = 'FAILURE';
      }
    } catch (err) {
      files[fileIndex].uploadState = 'FAILURE';
      setSelectedFiles([...files]);
    }
  }
  const pendingFilesCount = selectedFiles.filter((file) => file.progress !== 100).length;
  return (
    <>
      <VyModal open={open} onClose={onClose} maxWidth={600}>
        {(onClose) => (
          <div className="flex flex-col">
            <div className="flex justify-between">
              <p className="pb-5 text-xl font-bold">{t('booth:upload')}</p>
              <Close onClick={onClose} className="cursor-pointer" />
            </div>
            <div className="container">
              <div {...getRootProps({ style: dropStyle() })} className="mb-2 cursor-pointer">
                <input {...getInputProps()} />
                <AttachFile className="mr-2" />
                <p>{t('booth:drag-files-here')}</p>
              </div>
              {!showUploadProgress && (
                <aside className="flex flex-row place-items-start">
                  {acceptedFiles.length > 0 &&
                    previews.map((preview) => (
                      <div key={preview} className="relative mr-1 border rounded-md border-slate-400" style={{ width: 40, height: 40 }}>
                        <Image fill alt="upload preview" src={preview} className="object-cover rounded-md" />
                      </div>
                    ))}
                </aside>
              )}
              {showUploadProgress && (
                <aside className="flex flex-col place-items-start">
                  {selectedFiles.length > 0 ? (
                    selectedFiles.map((file) => (
                      <div key={file.src} className="flex flex-row items-center w-full">
                        <div key={file.size} className="relative border rounded-md border-slate-400" style={{ width: 40, height: 40 }}>
                          <video
                            onError={(event) => (event.currentTarget.style.display = 'none')}
                            width="49"
                            height="40"
                            src={file.src}
                            className="object-cover rounded-md"
                            style={{ height: 38 }}
                          />
                          {file.src && (
                            <Image
                              fill
                              onError={(event) => (event.currentTarget.style.display = 'none')}
                              alt="upload preview"
                              src={file.src}
                              className="object-cover rounded-md"
                            />
                          )}

                          {file.type === 'application/pdf' && (
                            <>
                              {isFirefox ? (
                                <div className="flex items-center justify-center h-full">
                                  <IoMdDocument size={28} />
                                </div>
                              ) : (
                                <embed src={file.src} style={{ width: 40, height: 40 }} className="object-cover rounded-md" />
                              )}
                            </>
                          )}
                        </div>
                        <div className="flex flex-col w-full p-2">
                          <div className="font-bold text-gray-700">{file.name}</div>
                          <div className="flex flex-row items-center space-x-4">
                            <ProgressBar max={100} filled={file.progress || 0} />
                            {!file.uploadState ? (
                              <div className="p-2 ml-2 font-mono text-gray-700 animate-pulse">{file.progress ?? 0}%</div>
                            ) : (
                              StatusIcon(file.uploadState)
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <></>
                  )}
                </aside>
              )}
            </div>
            {withUrl && (
              <>
                <div className="flex flex-row items-center m-5 mb-5">
                  <div className="w-full h-px mr-2 bg-slate-400" />
                  {t('booth:or')}
                  <div className="w-full h-px ml-2 bg-slate-400" />
                </div>
                <div className="flex items-center">
                  <VyTextField
                    size="small"
                    name="welcomeVideo"
                    fullWidth
                    label="Url"
                    value={currentUrl}
                    onChange={(change) => {
                      setCurrentUrl(change.target.value);
                    }}
                  />
                  {currentUrl ? (
                    <div className="relative self-end ml-2 border rounded-md border-slate-400" style={{ width: 40, height: 40 }}>
                      <Image
                        fill
                        src={currentUrl}
                        className="object-cover rounded-md"
                        onError={(e) => {
                          const target = e.target as HTMLTextAreaElement;
                          target.style.display = 'none';
                          setUrlValid(false);
                        }}
                        onLoad={(e) => {
                          const target = e.target as HTMLTextAreaElement;
                          target.style.display = 'inline';
                          clearSelection();
                          setUrlValid(true);
                        }}
                        alt="upload preview"
                      />
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              </>
            )}
            {withCrop && currentCropFileIndex >= 0 && (
              <Popup
                nested
                modal
                lockScroll
                open={currentCropFileIndex >= 0}
                onClose={() => {}}
                overlayStyle={{
                  zIndex: 1400,
                }}>
                <VyCropper
                  open={true}
                  image={selectedFiles[currentCropFileIndex].src ?? ''}
                  aspect={cropAspectRatio}
                  onCrop={(image) => onCropImage(image)}
                  onCancel={() => {}}
                />
              </Popup>
            )}
            <p className="mt-5 mb-2 text-center text-red-600">{error}</p>
            <div className="flex justify-center space-x-2">
              <VyButton
                disabled={showUploadProgress ? pendingFilesCount < 1 || isUploading : false}
                className="self-center"
                onClick={showUploadProgress ? handleProgressiveUpload : handleUpload}>
                {t('booth:upload')} {showUploadProgress && `(${pendingFilesCount})`}
              </VyButton>
              <VyButton disabled={pendingFilesCount > 0 || selectedFiles?.length === 0 || isUploading} className="self-center" onClick={onClose}>
                {t('booth:finish')}
              </VyButton>
            </div>
          </div>
        )}
      </VyModal>
    </>
  );
};

export default VyUploadModal;
